<template>
  <div id="ret-calc-ud">
    <form @submit.prevent="$emit('click')">
      <loader v-if="loading" />
      <div v-else class="text-left md:w-[900px] m-auto">
        <h5 class="mh5 md:h5 mb-8 text-left">Super details</h5>
        <div class="button-2 flex gap-6 my-4 items-center">
          <div class="set-w-28 md:set-w-58" />
          <div class="w-full">YOUR DETAILS</div>
          <div class="w-full flex gap-3 items-center">
            <input-check-box
              id="enable-partner-checkbox"
              data-test="showPartnerDetails"
              :checked="enablePartnerForm"
              @click="togglePartnerForm"
              name="enable partner check" />
            <span>PARTNER DETAILS</span>
          </div>
        </div>
        <div class="flex gap-6 my-6">
          <div class="flex gap-1 pt-4 set-w-28 md:set-w-58">
            <input-label label="Current age" class="paragraph-2" />
            <info-circle css-class="cfs-tooltip" fill="cfs-blue" message="How old are you today?" />
          </div>
          <input-number
            name="Retirement Calculator Age"
            place-holder="Age"
            data-test="userCurrentAge"
            :value="userData.age"
            @input="updateUserData($event, 'age')"
            :min="18"
            :max="87"
            class="w-full"
            :error="userErrors.age"
            @blur="setPreservationAge()" />
          <input-number
            name="Retirement Calculator Partner Age"
            place-holder="Age"
            data-test="partnerCurrentAge"
            :value="partnerData.age"
            @input="updatePartnerData($event, 'age')"
            :min="18"
            :max="87"
            v-if="enablePartnerForm"
            :disabled="!enablePartnerForm"
            class="w-full"
            :error="partnerErrors.age"
            @blur="setPreservationAge(true)" />
        </div>
        <div class="flex gap-6 my-6">
          <div class="flex gap-1 pt-4 set-w-28 md:set-w-58">
            <input-label label="Current salary (before tax)" class="paragraph-2" />
            <info-circle
              css-class="cfs-tooltip"
              fill="cfs-blue"
              message="The total cash salary including the tax you must pay, plus super received." />
          </div>
          <div class="w-full flex flex-col md:flex-row gap-3">
            <input-currency
              :value="userData.income"
              class="w-full"
              name="Retirement Calculator Income"
              data-test="userIncome"
              :error="userErrors.income"
              @blur="getNetSalary()"
              @input="updateUserData($event, 'income')" />
            <input-select-box
              :options="incomePeriods"
              :select-input="incomeFrequencySelectInput(userData.income_time_period)"
              class="w-full"
              name="User Income Period"
              data-test="userIncomeFrequency"
              @update-select-box="updateUserData($event, 'income_time_period')" />
          </div>
          <div class="w-full flex flex-col md:flex-row gap-3" v-if="enablePartnerForm">
            <input-currency
              :disabled="!enablePartnerForm"
              :value="partnerData.income"
              class="w-full"
              name="Retirement Calculator Income"
              data-test="partnerIncome"
              :error="partnerErrors.income"
              @blur="getNetSalary(true)"
              @input="updatePartnerData($event, 'income')" />
            <input-select-box
              name="User Income Period"
              class="w-full"
              data-test="partnerIncomeFrequency"
              :disabled="!enablePartnerForm"
              :select-input="incomeFrequencySelectInput(partnerData.income_time_period)"
              @update-select-box="updatePartnerData($event, 'income_time_period')"
              :options="incomePeriods" />
          </div>
        </div>
        <div class="flex gap-6 my-6">
          <div class="flex gap-1 pt-4 set-w-28 md:set-w-58">
            <input-label label="Current super balance" class="paragraph-2" />
            <info-circle
              css-class="cfs-tooltip"
              fill="cfs-blue"
              message="If you have more than one super account, enter the total amount of all your super accounts combined." />
          </div>
          <input-currency
            name="Retirement Calculator Super Balance"
            data-test="userSuperBalance"
            :value="userData.current_super_balance"
            @input="updateUserData($event, 'current_super_balance')"
            :error="userErrors.current_super_balance"
            class="w-full" />
          <input-currency
            name="Retirement Calculator Partner Super Balance"
            data-test="partnerSuperBalance"
            :value="partnerData.current_super_balance"
            @input="updatePartnerData($event, 'current_super_balance')"
            :error="partnerErrors.current_super_balance"
            class="w-full"
            v-if="enablePartnerForm"
            :disabled="!enablePartnerForm" />
        </div>
        <div class="flex gap-6 my-6">
          <div class="flex gap-1 pt-4 set-w-28 md:set-w-58">
            <input-label label="Employer contribution rate" class="paragraph-2" />
            <info-circle
              css-class="cfs-tooltip"
              fill="cfs-blue"
              message="The % of your salary that your employer adds to your super." />
          </div>
          <input-percentage
            name="Retirement Calculator Contribution rate"
            :place-holder="
              getMinSuperContributionRate()
                ? formatPercentage(getMinSuperContributionRate())
                : 'Loading...'
            "
            data-test="userContributionRate"
            :value="userData.contribution_rate"
            @input="updateEmployerContributionRate(false, $event)"
            class="w-full"
            :info="
              getMinSuperContributionRate() &&
              userData.contribution_rate < getMinSuperContributionRate()
                ? `Zero applies when there’s no employer contributing to your super. If there is the employer rate is ${formatPercentage(
                    getMinSuperContributionRate()
                  )}`
                : ''
            "
            :max="25" />
          <input-percentage
            name="Retirement Calculator Partner Contribution rate"
            data-test="partnerContributionRate"
            :place-holder="
              getMinSuperContributionRate()
                ? formatPercentage(getMinSuperContributionRate())
                : 'Loading...'
            "
            :value="partnerData.contribution_rate"
            @input="updateEmployerContributionRate(true, $event)"
            class="w-full"
            v-if="enablePartnerForm"
            :disabled="!enablePartnerForm"
            :info="
              getMinSuperContributionRate() &&
              partnerData.contribution_rate < getMinSuperContributionRate()
                ? `Zero applies when there’s no employer contributing to your super. If there is the employer rate is ${formatPercentage(
                    getMinSuperContributionRate()
                  )}`
                : ''
            "
            :max="25" />
        </div>

        <div class="flex gap-6 my-6">
          <div class="set-w-28 md:set-w-58 pt-4 paragraph-2">
            <input-label label="Target annual retirement income (in today dollars)" />
            <info-circle
              id="ret-income"
              css-class="cfs-tooltip"
              fill="cfs-blue"
              message="The yearly income you  would like to live on when you retire. Most people enter a figure that's around 70% of their working life household income after tax. Yours would be $36,331 (70% of $51,902)."
              class="ml-1" />
          </div>
          <input-currency
            name="Retirement Calculator Retirement Income"
            data-test="targetRetirementIncome"
            place-holder="Normally 70% of your net income"
            :value="userData.desired_retirement_income"
            @input="updateUserData($event, 'desired_retirement_income')"
            :error="userErrors.desired_retirement_income"
            class="w-full" />
        </div>
        <div class="flex gap-6 mt-6">
          <div class="set-w-28 md:set-w-58 pt-4 paragraph-2">
            <input-label label="Retirement age" />
            <info-circle
              css-class="cfs-tooltip"
              fill="cfs-blue"
              message="The age you want to stop working."
              class="ml-1" />
          </div>
          <input-number
            name="Retirement Calculator Retirement Age"
            data-test="userRetirementAge"
            place-holder="Age"
            :value="userData.desired_retirement_age"
            :default-value="67"
            @input="updateUserData($event, 'desired_retirement_age')"
            :error="userErrors.desired_retirement_age"
            class="w-full"
            :min="18"
            :max="87" />
          <input-number
            name="Retirement Calculator Partner Retirement Age"
            data-test="partnerRetirementAge"
            place-holder="Age"
            :value="partnerData.desired_retirement_age"
            :default-value="67"
            @input="updatePartnerData($event, 'desired_retirement_age')"
            :error="partnerErrors.desired_retirement_age"
            class="w-full"
            v-if="enablePartnerForm"
            :disabled="!enablePartnerForm"
            :min="18"
            :max="87" />
        </div>
        <div class="flex gap-6 mb-6">
          <div class="set-w-28 md:set-w-58" />
          <input-label
            class="paragraph-2 ml-4 mt-2"
            v-if="userData.super_access_age !== null"
            :label="
              `You can access your super at ${userData.super_access_age}` +
              (enablePartnerForm
                ? `, your partner can access their super at ${partnerData.super_access_age} `
                : '')
            " />
        </div>
        <div class="flex gap-6 my-6">
          <div class="set-w-28 md:set-w-58 pt-4 paragraph-2">
            <input-label label="Do you own your home" />
            <info-circle
              css-class="cfs-tooltip"
              fill="cfs-blue"
              message="This may alter your age pension payment."
              class="ml-1" />
          </div>
          <input-select-box
            name="Owns home"
            data-test="ownsHome"
            class="w-full"
            @update-select-box="updateUserData($event, 'hasHome')"
            :select-input="
              userData.hasHome !== null
                ? {
                    label: userData.hasHome ? 'Yes' : 'No',
                    value: userData.hasHome,
                  }
                : { label: '-', value: null }
            "
            :reset-button="true"
            :options="[
              {
                label: '-',
                value: null,
                disabled: true,
              },
              {
                label: 'Yes',
                value: true,
                id: 'Yes',
              },
              {
                label: 'No',
                value: false,
                id: 'No',
              },
            ]" />
        </div>
      </div>
      <div class="flex justify-center m-auto my-12 items-center gap-4">
        <custom-button
          id="update-graph-data-button"
          data-test="nextBtn"
          type="submit"
          colour="cfs-button"
          class="rounded-full p-5 w-44 button-2"
          :loading="graphLoading">
          {{ displaySummary ? 'Update' : 'Next' }}
        </custom-button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { format, subYears } from 'date-fns'
import InputCurrency from '@/components/BasicInputs/InputCurrency.vue'
import InputPercentage from '@/components/BasicInputs/InputPercentage.vue'
import InputNumber from '@/components/BasicInputs/InputNumber.vue'
import CustomButton from '@/components/CustomButton/CustomButton.vue'
import InputLabel from '@/components/BasicInputs/InputLabel.vue'
import InputCheckBox from '@/components/BasicInputs/InputCheckBox.vue'
import InputSelectBox from '@/components/BasicInputs/InputSelectBox.vue'
import InfoCircle from '@/components/InfoCircle/InfoCircle.vue'
import Loader from '@/components/Loader/Loader.vue'
import { formatCurrency, formatPercentage } from '@/lib/helpers'
import RetCalcService from '@/services/RetCalcService'
import { useRetirementCalculator } from '@/composables/useRetirementCalculator'
import { useRetirementCalculatorStore } from '@/store/pinia/RetirementCalculatorStore'
import { useRetirementCalculatorErrorStore } from '@/store/pinia/RetirementCalculatorErrorStore'

interface IncomePeriod {
  label: string
  value: string
}

interface Props {
  loading?: boolean
}

// Props and emits
const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const emit = defineEmits<{
  loaded: []
  click: []
}>()

// Store setup
const retirementCalculatorStore = useRetirementCalculatorStore()
const errorStore = useRetirementCalculatorErrorStore()
const { getMinSuperContributionRate } = useRetirementCalculator()

// Reactive data
const incomePeriods = ref<IncomePeriod[]>([
  { label: 'Yearly', value: 'yearly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Fortnightly', value: 'fortnightly' },
  { label: 'Weekly', value: 'weekly' },
])

// Computed properties from stores
const userData = computed(() => retirementCalculatorStore.getUserData)
const partnerData = computed(() => retirementCalculatorStore.getPartnerData)
const enablePartnerForm = computed(() => retirementCalculatorStore.getEnablePartnerForm)
const graphLoading = computed(() => retirementCalculatorStore.getGraphLoading)
const displaySummary = computed(() => retirementCalculatorStore.getDisplaySummary)
const disclaimer = computed(() => retirementCalculatorStore.getDisclaimer)

// Error computed properties
const userErrors = computed(() => errorStore.getUserErrors)
const partnerErrors = computed(() => errorStore.getPartnerErrors)

// Methods
const updateUserData = (value: any, field: string): void => {
  retirementCalculatorStore.updateUserDataKeyPair({ field, value })
}

const updatePartnerData = (value: any, field: string): void => {
  retirementCalculatorStore.updatePartnerDataKeyPair({ field, value })
}

const togglePartnerForm = async (): Promise<void> => {
  await retirementCalculatorStore.togglePartnerForm()
}

const updateEmployerContributionRate = (partner: boolean, value: number): void => {
  if (partner) {
    updatePartnerData(value, 'contribution_rate')
    getNetSalary(true)
  } else {
    updateUserData(value, 'contribution_rate')
    getNetSalary(false)
  }
}

const getTotalIncome = (): number => {
  let salary = 0
  salary += userData.value.annual_net_income || 0
  if (enablePartnerForm.value) salary += partnerData.value.annual_net_income || 0
  return salary
}

const getNetSalary = async (isPartner = false): Promise<void> => {
  const objectVariable = isPartner ? partnerData.value : userData.value
  let income = objectVariable.income || 0

  if (objectVariable.income_time_period === 'weekly') {
    income = income * 52
  } else if (objectVariable.income_time_period === 'fortnightly') {
    income = income * 26
  } else if (objectVariable.income_time_period === 'monthly') {
    income = income * 12
  }

  const payload = {
    gas: income,
    desRetInc: isPartner ? partnerData.value.contribution_rate : userData.value.contribution_rate,
    addCont: isPartner
      ? partnerData.value.before_tax_contribution
      : userData.value.before_tax_contribution,
    isPartner: isPartner,
  }

  try {
    const res = await RetCalcService.getNetIncome(payload)
    const updateMethod = isPartner ? updatePartnerData : updateUserData
    updateMethod(res.data.netSalaryPerAnnum, 'annual_net_income')
    updateUserData(getSalaryPlaceHolder(), 'desired_retirement_income')

    const labelPayload = {
      desiredRetIncome: getSalaryPlaceHolder(),
      hasPartner: enablePartnerForm.value,
      userGAS: isPartner ? userData.value.income : payload.gas,
      partnerGAS: isPartner ? payload.gas : partnerData.value.income,
    }

    // Label content dispatch removed - handled by Pinia store
  } catch (error) {
    console.error('Error getting net salary:', error)
  }
}

const getSalaryPlaceHolder = (): number => {
  return getTotalIncome() > 0 ? Math.round(getTotalIncome() * 0.7) : 50000
}

const setPreservationAge = async (isPartner = false): Promise<void> => {
  const date = new Date()
  date.setMonth(1)
  const targetData = isPartner ? partnerData.value : userData.value
  const age = targetData.age || 0

  try {
    const birthDate = format(subYears(date, age), 'yyyy-MM-dd')
    const preservationAge = await retirementCalculatorStore.getPreservationAgeForBirthDate(
      birthDate,
      !isPartner
    )

    const updateMethod = isPartner ? updatePartnerData : updateUserData
    updateMethod(preservationAge, 'super_access_age')
  } catch (error) {
    console.error('Error setting preservation age:', error)
  }
}

const incomeFrequencySelectInput = (frequency: string) => {
  return incomePeriods.value.find((option) => option.value === frequency)
}

const getDisclaimer = async (): Promise<void> => {
  await retirementCalculatorStore.fetchDisclaimer()
}

// Lifecycle
onMounted(async (): Promise<void> => {
  await getDisclaimer()
  emit('loaded')
})
</script>

<style scoped></style>
